import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getServicesQueryOptions } from '@/services/api';
import type { CustomFormResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, SHARED_QUERY_OPTIONS } from './constants';
import {
  filterCustomForms,
  filterFacebookLeadAdsForms,
  mapCustomFormToSearchResult,
  mapFacebookLeadAdsFormToSearchResult,
} from './utils/form-results';
import { mergeQueryStates, type SearchSource } from './utils/source-bookkeeping';

type UseFormsSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type FormsSource = SearchSource<CustomFormResult>;

export function useFormsSource({ workspaceId, enabled, query }: UseFormsSourceOptions): FormsSource {
  const [formsQuery, facebookLeadAdsFormsQuery] = useQueries({
    queries: [
      getServicesQueryOptions(
        'getCustomForms',
        { params: { environment_id: workspaceId } },
        {
          enabled,
          ...SHARED_QUERY_OPTIONS,
          select: (data) => data.custom_forms,
        },
      ),
      getServicesQueryOptions(
        'getFacebookLeadAdsForms',
        { params: { environment_id: workspaceId } },
        {
          enabled,
          ...SHARED_QUERY_OPTIONS,
          select: (data) => data.facebook_lead_ads_forms,
        },
      ),
    ],
  });

  const sourceState = mergeQueryStates([formsQuery, facebookLeadAdsFormsQuery]);
  const results = useMemo(
    () =>
      [
        ...filterCustomForms(formsQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((form) =>
          mapCustomFormToSearchResult(form, workspaceId),
        ),
        ...filterFacebookLeadAdsForms(facebookLeadAdsFormsQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((form) =>
          mapFacebookLeadAdsFormToSearchResult(form, workspaceId),
        ),
      ].slice(0, CLIENT_SIDE_RESULT_CAP),
    [formsQuery.data, facebookLeadAdsFormsQuery.data, query, workspaceId],
  );

  return { ...sourceState, results };
}
